import arrowDownSmall from "@amzn/meridian-tokens/base/icon/arrow-down-small";
import arrowUpSmall from "@amzn/meridian-tokens/base/icon/arrow-up-small";
import Button from "@amzn/meridian/button";
import Card, { CardActionBar, CardHeader } from "@amzn/meridian/card";
import Column from "@amzn/meridian/column";
import Divider from "@amzn/meridian/divider";
import Heading from "@amzn/meridian/heading";
import Icon from "@amzn/meridian/icon";
import Loader from "@amzn/meridian/loader";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import React, { useEffect, useState } from "react";
import TimePeriodService, { TimePeriodType } from "src/services/timePeriodService";
import { capitalize } from "src/utils/stringUtil";
import { KeysOfType } from "src/utils/types";
import { Count, InsightsForm } from "../types";
import { formatPercent } from "src/utils/numberUtil";

interface InsightHeading {
  label: string;
  data?: string | number;
  change?: number;
}
interface InsightsCardProps {
  counts: Count[];
  isLoading: boolean;
  averageAccuracy: number;
  periodTypeOptions: TimePeriodType[];
  insightsForm: InsightsForm;
  onSelectPeriodType: (selectedPeriodType: TimePeriodType) => void;
  onSelectPeriodIndex: (index: number) => void;
  onReviewClick: () => void;
  supportsReview?: boolean;
}

const InsightsCard = ({
  counts,
  averageAccuracy,
  periodTypeOptions,
  insightsForm,
  isLoading,
  onSelectPeriodIndex,
  onSelectPeriodType,
  onReviewClick,
  supportsReview = true,
}: InsightsCardProps) => {
  const [headings, setHeadings] = useState<InsightHeading[]>([]);
  const isReviewDisabled = !counts[insightsForm.selectedCountIndex]?.totalCount;

  useEffect(() => {
    if (!counts[insightsForm.selectedCountIndex]) return;

    setHeadings([
      {
        label: "Total No. of Records Generated",
        data: counts[insightsForm.selectedCountIndex].totalCount,
        change: getInsightChange("totalCount"),
      },
      {
        label: "Total No. of Records Reviewed",
        data: counts[insightsForm.selectedCountIndex].reviewedCount,
        change: getInsightChange("reviewedCount"),
      },
      ...(supportsReview
        ? [{ label: "Accuracy Rates of Insights", data: formatPercent(averageAccuracy) }]
        : []),
    ]);
  }, [insightsForm, counts, averageAccuracy, supportsReview]);

  const getInsightChange = (field: KeysOfType<Count, number>) => {
    const lastWeekData = counts.length ? counts[insightsForm.selectedCountIndex + 1] : null;
    if (lastWeekData) return counts[insightsForm!.selectedCountIndex][field] - lastWeekData[field];
  };

  // e.g "Monthly" => "month"
  const getPeriodTypeLabel = () => {
    return insightsForm.selectedPeriodType.replace("ly", "").toLowerCase();
  };

  return (
    <Card spacingInset={"500"}>
      <CardHeader>
        <Heading level={3}>Insights</Heading>
      </CardHeader>
      <Column alignmentVertical="center" spacing={"400"}>
        <Row alignmentVertical="center" widths={["fit", "grid-4"]}>
          <Select
            label="Period Type"
            value={insightsForm.selectedPeriodType}
            onChange={onSelectPeriodType}
            placeholder="Enter value..."
            width={300}
          >
            {periodTypeOptions.map((option) => (
              <SelectOption key={option} value={option} label={capitalize(option)} />
            ))}
          </Select>

          {!isLoading ? (
            <Select
              label="Period"
              value={insightsForm.selectedCountIndex}
              onChange={onSelectPeriodIndex}
              placeholder="Select period..."
              width={300}
            >
              {counts.map(({ periodStart, periodEnd }, index) => (
                <SelectOption
                  key={index}
                  value={index}
                  label={TimePeriodService.getPeriodLabel({
                    selectedPeriodType: insightsForm.selectedPeriodType,
                    periodStart,
                    periodEnd,
                  })}
                />
              ))}
            </Select>
          ) : (
            <Loader size="medium" />
          )}
        </Row>

        {counts.length ? (
          <Row alignmentVertical="top" spacingInset={"300"} height={"100%"} widths={"fill"}>
            {headings.map(({ label, data, change }) => (
              <Column key={label} spacing={"200"}>
                <Text color="secondary" type="h50">
                  {label}
                </Text>
                <Text type="h500">{data !== undefined ? data : "N/A"}</Text>
                <Row spacing={"100"}>
                  {change !== undefined ? (
                    <>
                      {change ? (
                        <Icon
                          color={change > 0 ? "success" : "error"}
                          tokens={change > 0 ? arrowUpSmall : arrowDownSmall}
                        />
                      ) : (
                        ""
                      )}
                      <Text
                        color={change ? (change > 0 ? "success" : "error") : "warning"}
                        type="h50"
                      >
                        {change ? Math.abs(change) : "No change"} this {getPeriodTypeLabel()}
                      </Text>
                    </>
                  ) : (
                    ""
                  )}
                </Row>
              </Column>
            ))}
          </Row>
        ) : (
          ""
        )}
      </Column>
      {supportsReview && (
        <>
          <Divider spacingBefore={"400"} spacingAfter={"300"} />
          <CardActionBar>
            <Button type="tertiary" disabled={isReviewDisabled} onClick={onReviewClick}>
              Review
            </Button>

            <Text color="secondary">
              {!isReviewDisabled
                ? `Review a randomly selected case from the specified ${getPeriodTypeLabel()}`
                : "No cases to review"}
            </Text>
          </CardActionBar>
        </>
      )}
    </Card>
  );
};

export default InsightsCard;
